import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Select,
  Skeleton,
  Modal,
  DataTable,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { PURCHASE_CURRENCY_OPTIONS, formatDocAmount } from '@/utils/purchase-currency';
import {
  useSupplier,
  useUpdateSupplier,
  useDeactivateSupplier,
  useReactivateSupplier,
  useSupplierPurchaseOrders,
  SupplierStatusBadge,
  SupplierKpiCard,
  E164_REGEX,
  EMAIL_REGEX,
  NAME_MAX,
  CONTACT_PERSON_MAX,
  ADDRESS_MAX,
  CITY_MAX,
  STATE_MAX,
  NOTES_MAX,
  REASON_MAX,
  PAYMENT_TERMS_OPTIONS,
  PAYMENT_TERMS_LABELS,
  SUPPLIER_COUNTRY_OPTIONS,
  useSupplierCountry,
  suggestedSupplierCurrency,
} from '@/features/suppliers';
import type { UpdateSupplierPayload, SupplierPOListItem } from '@/features/suppliers';
import { POStatusBadge } from '@/features/purchase-orders';
import { SupplierReturnsPanel } from '@/features/purchase-returns';
import type { PurchaseOrderStatus } from '@/features/purchase-orders';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { useTenantCountry } from '@/hooks/use-tenant-country';
import { clampPostalInput, isValidPostalCode } from '@/utils/address-fields';

// ─── Icons ──────────────────────────────────────────────────────────────────

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function BanIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );
}

function CurrencyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function clip(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

function InfoRow({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between py-2.5 border-b border-neutral-50 dark:border-neutral-800 last:border-0">
      <span className="text-sm text-neutral-500 dark:text-neutral-400">{label}</span>
      <span className={`text-sm font-medium text-neutral-900 dark:text-white text-right ${mono ? 'font-mono tracking-wide' : ''}`}>
        {value || '\u2014'}
      </span>
    </div>
  );
}

// ─── Content ────────────────────────────────────────────────────────────────

function SupplierDetailContent() {
  const currency = useTenantCurrency();
  const tenantCountry = useTenantCountry();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);

  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isOwner = roleLevel >= ROLE_LEVELS.OWNER;

  const { data: supplier, isLoading, error } = useSupplier(id);
  // The supplier's own country (none = the tenant's) labels its tax id and
  // drives the edit form's tax id rule, address fields and dial code (I-3).
  // Qatar/Kuwait have no statutory id ⇒ the row/field is hidden.
  const supplierCountry = useSupplierCountry(supplier?.country_code);
  const updateMutation = useUpdateSupplier();
  const deactivateMutation = useDeactivateSupplier();
  const reactivateMutation = useReactivateSupplier();

  // PO history
  const [poPage, setPoPage] = useState(1);
  const { data: poData } = useSupplierPurchaseOrders(id, poPage, 10);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', contact_person: '', phone: '', email: '', country_code: tenantCountry.iso,
    currency_code: currency as string, gstin: '',
    address_line1: '', address_line2: '', city: '', state: '', pincode: '',
    payment_terms: 'NET_30', notes: '',
  });
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const editCountry = useSupplierCountry(editForm.country_code);
  const { addressFields } = editCountry;
  // The currency follows a new country only for a supplier with no purchase
  // orders, and only until someone picks one by hand (T21, owner decision).
  const [currencyPicked, setCurrencyPicked] = useState(false);
  const hasPurchaseOrders = (supplier?.total_po_count ?? 0) > 0;
  const keepsCurrencyForPOs =
    hasPurchaseOrders &&
    !currencyPicked &&
    editForm.country_code !== (supplier?.country_code || tenantCountry.iso) &&
    editForm.currency_code !== editCountry.suggestedCurrency;

  // Deactivate modal
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');

  // Reactivate modal
  const [showReactivateModal, setShowReactivateModal] = useState(false);

  useEffect(() => {
    if (error) {
      toast({ title: (error as Error).message, variant: 'error' });
    }
  }, [error]);

  function openEditModal() {
    if (!supplier) return;
    setEditForm({
      name: supplier.name,
      contact_person: supplier.contact_person || '',
      phone: supplier.phone ?? '',
      email: supplier.email || '',
      country_code: supplier.country_code || tenantCountry.iso,
      currency_code: supplier.currency_code || currency,
      gstin: supplier.gstin || '',
      address_line1: supplier.address_line1 || '',
      address_line2: supplier.address_line2 || '',
      city: supplier.city || '',
      state: supplier.state || '',
      pincode: supplier.pincode || '',
      payment_terms: supplier.payment_terms,
      notes: supplier.notes || '',
    });
    setEditErrors({});
    setCurrencyPicked(false);
    setShowEditModal(true);
  }

  function validateEdit(): boolean {
    const errs: Record<string, string> = {};
    if (!editForm.name.trim()) errs.name = 'Required';
    else if (editForm.name.length > NAME_MAX) errs.name = `Max ${NAME_MAX} characters`;
    if (editForm.contact_person.length > CONTACT_PERSON_MAX) {
      errs.contact_person = `Max ${CONTACT_PERSON_MAX} characters`;
    }
    if (editForm.phone && !E164_REGEX.test(editForm.phone)) errs.phone = 'Invalid phone number';
    if (editForm.email && !EMAIL_REGEX.test(editForm.email)) errs.email = 'Invalid email';
    if (editForm.gstin && editCountry.taxId && !editCountry.taxId.regex.test(editForm.gstin.toUpperCase())) {
      errs.gstin = `Must be a valid ${editCountry.taxId.label}`;
    }
    if (!isValidPostalCode(editForm.pincode, editCountry.iso)) {
      errs.pincode = `${addressFields.postalLabel} is not valid for ${editCountry.name}`;
    }
    if (editForm.notes.length > NOTES_MAX) errs.notes = `Max ${NOTES_MAX} characters`;
    setEditErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleEdit() {
    if (!id || !validateEdit()) return;
    const payload: UpdateSupplierPayload = {};
    if (editForm.name.trim()) payload.name = editForm.name.trim();
    // An emptied field goes as null, which clears it; undefined is dropped by
    // JSON and the old value would stay (PU1-001).
    if (editForm.contact_person !== (supplier?.contact_person || '')) payload.contact_person = editForm.contact_person.trim() || null;
    if (editForm.phone !== (supplier?.phone ?? '')) payload.phone = editForm.phone || null;
    if (editForm.email !== (supplier?.email || '')) payload.email = editForm.email.trim() || null;
    if (editForm.gstin !== (supplier?.gstin || '')) payload.gstin = editForm.gstin.toUpperCase().trim() || null;
    // The API checks a tax id against the country sent with it, so send the
    // country whenever either changes. A country with no tax id (Kuwait, Qatar)
    // clears the stored one, which would no longer suit it.
    if (editForm.country_code !== (supplier?.country_code || tenantCountry.iso) || payload.gstin !== undefined) {
      payload.country_code = editForm.country_code;
      if (!editCountry.taxId && supplier?.gstin) payload.gstin = null;
    }
    if (editForm.currency_code !== (supplier?.currency_code || currency)) payload.currency_code = editForm.currency_code;
    if (editForm.address_line1 !== (supplier?.address_line1 || '')) payload.address_line1 = editForm.address_line1.trim() || null;
    if (editForm.address_line2 !== (supplier?.address_line2 || '')) payload.address_line2 = editForm.address_line2.trim() || null;
    if (editForm.city !== (supplier?.city || '')) payload.city = editForm.city.trim() || null;
    if (editForm.state !== (supplier?.state || '')) payload.state = editForm.state || null;
    if (editForm.pincode !== (supplier?.pincode || '')) payload.pincode = editForm.pincode.trim() || null;
    if (editForm.payment_terms !== supplier?.payment_terms) payload.payment_terms = editForm.payment_terms as UpdateSupplierPayload['payment_terms'];
    if (editForm.notes !== (supplier?.notes || '')) payload.notes = editForm.notes.trim() || null;

    updateMutation.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast({ title: 'Supplier updated', variant: 'success' });
          setShowEditModal(false);
        },
        onError: (err: Error) => {
          toast({ title: err.message, variant: 'error' });
        },
      },
    );
  }

  function handleDeactivate() {
    if (!id) return;
    deactivateMutation.mutate(
      { id, reason: deactivateReason.trim() || undefined },
      {
        onSuccess: () => {
          toast({ title: 'Supplier deactivated', variant: 'success' });
          setShowDeactivateModal(false);
          setDeactivateReason('');
        },
        onError: (err: Error) => {
          toast({ title: err.message, variant: 'error' });
        },
      },
    );
  }

  function handleReactivate() {
    if (!id) return;
    reactivateMutation.mutate(id, {
      onSuccess: () => {
        toast({ title: 'Supplier reactivated', variant: 'success' });
        setShowReactivateModal(false);
      },
      onError: (err: Error) => {
        toast({ title: err.message, variant: 'error' });
      },
    });
  }

  const poColumns: ColumnDef<SupplierPOListItem>[] = [
    {
      key: 'po_number',
      header: 'PO #',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => navigate(`/inventory/purchase-orders/${row.id}`)}
          className="text-sm text-teal-600 dark:text-teal-400 hover:underline font-medium tabular-nums"
        >
          {row.po_number}
        </button>
      ),
    },
    {
      key: 'location_name',
      header: 'Location',
      render: (_v, row) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400">{row.location_name}</span>
      ),
    },
    {
      key: 'total_amount',
      header: 'Amount',
      numeric: true,
      render: (_v, row) => (
        // A PO in the supplier's currency shows it, then the tenant figure (like the PO list).
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
          {row.currency_code
            ? formatDocAmount(Number(row.total_amount), row.currency_code)
            : formatCurrency(Number(row.total_amount), currency)}
          {row.currency_code && row.base_total_amount != null && (
            <span className="block text-xs font-normal text-neutral-500 dark:text-neutral-400">
              ≈ {formatCurrency(Number(row.base_total_amount), currency)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => <POStatusBadge status={row.status as PurchaseOrderStatus} />,
    },
    {
      key: 'created_at',
      header: 'Date',
      render: (_v, row) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400 tabular-nums">
          {new Date(row.created_at).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
          })}
        </span>
      ),
    },
  ];

  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoading && !supplier) {
    return (
      <>
        <Skeleton variant="rect" height={64} className="rounded-xl mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Skeleton variant="rect" height={400} className="rounded-xl" />
          </div>
          <Skeleton variant="rect" height={250} className="rounded-xl" />
        </div>
      </>
    );
  }

  if (!supplier) {
    return (
      <div className="text-center py-20">
        <p className="text-neutral-500 dark:text-neutral-400">Supplier not found</p>
        <Button variant="secondary" onClick={() => navigate('/inventory/suppliers')} className="mt-4">
          Back to Suppliers
        </Button>
      </div>
    );
  }

  const address = [supplier.address_line1, supplier.address_line2, supplier.city, supplier.state, supplier.pincode]
    .filter(Boolean)
    .join(', ');
  // A stored id is shown even where the country has none (e.g. entered before
  // the supplier's country was set), under a generic label.
  const taxIdLabel = supplierCountry.taxId?.label ?? (supplier.gstin ? 'Tax ID' : null);

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => navigate('/inventory/suppliers')}
            className="mt-1 p-2 rounded-lg text-neutral-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
            aria-label="Back to suppliers"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div className="border-l-4 border-teal-500 pl-4">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold text-neutral-900 dark:text-white">{supplier.name}</h1>
              <SupplierStatusBadge isActive={supplier.is_active} />
            </div>
            {supplier.contact_person && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">{supplier.contact_person}</p>
            )}
          </div>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            {supplier.is_active ? (
              <>
                <Button variant="secondary" onClick={openEditModal}>
                  <PencilIcon className="h-4 w-4 mr-1.5" />
                  Edit
                </Button>
                <Button variant="danger" onClick={() => setShowDeactivateModal(true)}>
                  <BanIcon className="h-4 w-4 mr-1.5" />
                  Deactivate
                </Button>
              </>
            ) : (
              <Button variant="primary" onClick={() => setShowReactivateModal(true)}>
                <RefreshIcon className="h-4 w-4 mr-1.5" />
                Reactivate
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Inactive banner */}
      {!supplier.is_active && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-warning-200 bg-warning-50 dark:border-warning-900/40 dark:bg-warning-900/10 p-4 animate-fade-in">
          <AlertIcon className="h-5 w-5 text-warning-600 dark:text-warning-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-warning-800 dark:text-warning-200">
              This supplier is deactivated
            </p>
            <p className="text-xs text-warning-700 dark:text-warning-300 mt-0.5">
              They will not appear when creating new purchase orders. Reactivate to resume procurement.
            </p>
          </div>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Left: Supplier Info */}
        <div className="lg:col-span-2 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm rounded-xl shadow-md p-6 space-y-6">
          {/* Contact */}
          <section>
            <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">Contact</h3>
            <InfoRow label="Phone" value={supplier.phone} mono />
            <InfoRow label="Email" value={supplier.email} />
          </section>

          {/* Tax & Payment */}
          <section>
            <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">Tax & Payment</h3>
            {taxIdLabel && <InfoRow label={taxIdLabel} value={supplier.gstin} mono />}
            <InfoRow label="Payment Terms" value={PAYMENT_TERMS_LABELS[supplier.payment_terms] ?? supplier.payment_terms} />
            <InfoRow label="Currency" value={supplier.currency_code || currency} />
          </section>

          {/* Address */}
          <section>
            <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">Address</h3>
            <InfoRow label="Country" value={supplierCountry.name} />
            <p className="text-sm text-neutral-900 dark:text-white">{address || '\u2014'}</p>
          </section>

          {/* Notes */}
          {supplier.notes && (
            <section>
              <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">Notes</h3>
              <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{supplier.notes}</p>
            </section>
          )}

          {/* Meta */}
          <section className="pt-2 border-t border-neutral-100 dark:border-neutral-800">
            <InfoRow
              label="Created"
              value={new Date(supplier.created_at).toLocaleDateString('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            />
            {supplier.created_by_name && (
              <InfoRow label="Created By" value={supplier.created_by_name} />
            )}
          </section>
        </div>

        {/* Right: Procurement Stats — uses the same KPI visual language as the list page */}
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
          <SupplierKpiCard
            title="Total POs"
            value={(supplier.total_po_count ?? 0).toLocaleString('en-IN')}
            tone="teal"
            icon={<ClipboardIcon className="h-5 w-5" />}
          />
          <SupplierKpiCard
            title="Total Spend"
            value={formatCurrency(supplier.total_spend ?? 0, currency)}
            tone="success"
            icon={<CurrencyIcon className="h-5 w-5" />}
            hint="Ordered and received POs. Drafts and cancelled POs are not counted."
          />
          <SupplierKpiCard
            title="Outstanding payable"
            value={formatCurrency(supplier.outstanding_payable ?? 0, currency)}
            tone={(supplier.outstanding_payable ?? 0) > 0 ? 'warning' : 'success'}
            icon={<CurrencyIcon className="h-5 w-5" />}
            hint={[
              // Open debit-note credit is already taken off (QA PU3-005).
              (supplier.supplier_credit ?? 0) > 0 ? `After ${formatCurrency(supplier.supplier_credit ?? 0, currency)} supplier credit from returns` : null,
              supplier.fx_gain_loss
                ? `Exchange ${supplier.fx_gain_loss > 0 ? 'gain' : 'loss'} to date: ${formatCurrency(Math.abs(supplier.fx_gain_loss), currency)}`
                : null,
            ].filter(Boolean).join(' · ') || undefined}
          />
          <SupplierKpiCard
            title="Last PO"
            value={supplier.last_po_date
              ? new Date(supplier.last_po_date).toLocaleDateString('en-IN', {
                  day: '2-digit', month: 'short', year: 'numeric',
                })
              : '\u2014'}
            tone="brand"
            icon={<CalendarIcon className="h-5 w-5" />}
          />
        </div>
      </div>

      {/* PO History */}
      <div className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm rounded-xl shadow-md">
        <div className="px-6 py-4 border-b border-neutral-100 dark:border-neutral-800">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Purchase Order History</h3>
        </div>
        <DataTable<SupplierPOListItem>
          columns={poColumns}
          data={poData?.data ?? []}
          totalCount={poData?.meta?.total ?? 0}
          currentPage={poPage}
          pageSize={10}
          onPageChange={setPoPage}
          emptyState={{
            title: 'No purchase orders',
            description: 'Purchase orders linked to this supplier will appear here',
          }}
        />
      </div>

      {id && <SupplierReturnsPanel supplierId={id} isOwner={isOwner} />}

      {/* Edit Modal */}
      <Modal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Supplier"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleEdit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              variant="searchable"
              label="Country of registration"
              options={SUPPLIER_COUNTRY_OPTIONS}
              value={editForm.country_code}
              onChange={(v) => {
                if (typeof v !== 'string' || !v || v === editForm.country_code) return;
                // A new country brings its own region list; the old pick no longer applies.
                const followCountry = !hasPurchaseOrders && !currencyPicked;
                setEditForm((f) => ({
                  ...f,
                  country_code: v,
                  state: '',
                  currency_code: followCountry ? suggestedSupplierCurrency(v, tenantCountry.iso, currency) : f.currency_code,
                }));
                setEditErrors((e) => ({ ...e, gstin: '', pincode: '' }));
              }}
            />
            <div>
              <Select
                variant="searchable"
                label="Currency"
                options={PURCHASE_CURRENCY_OPTIONS}
                value={editForm.currency_code}
                onChange={(v) => {
                  if (typeof v !== 'string' || !v) return;
                  setCurrencyPicked(true);
                  setEditForm((f) => ({ ...f, currency_code: v }));
                }}
              />
              {keepsCurrencyForPOs && (
                <p className="text-xs text-warning-600 dark:text-warning-400 mt-1">
                  Currency stays {editForm.currency_code} because this supplier has purchase orders — change it by hand if needed.
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Supplier Name <span className="text-danger-500">*</span>
              </label>
              <Input
                value={editForm.name}
                onChange={(v) => setEditForm((f) => ({ ...f, name: clip(v, NAME_MAX) }))}
              />
              {editErrors.name && <p className="text-xs text-danger-500 mt-1">{editErrors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Contact Person</label>
              <Input
                value={editForm.contact_person}
                onChange={(v) => setEditForm((f) => ({ ...f, contact_person: clip(v, CONTACT_PERSON_MAX) }))}
              />
              {editErrors.contact_person && <p className="text-xs text-danger-500 mt-1">{editErrors.contact_person}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Phone</label>
              <Input
                variant="phone"
                defaultDial={editCountry.dial}
                value={editForm.phone}
                onChange={(v) => setEditForm((f) => ({ ...f, phone: v }))}
                placeholder="98765 43210"
              />
              {editErrors.phone && <p className="text-xs text-danger-500 mt-1">{editErrors.phone}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Email</label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(v) => setEditForm((f) => ({ ...f, email: v }))}
                inputMode="email"
              />
              {editErrors.email && <p className="text-xs text-danger-500 mt-1">{editErrors.email}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Statutory tax id of the supplier's country — `GSTIN` (India) /
                `TRN`·`VATIN`·… (GCC) / "Tax ID" elsewhere. Qatar/Kuwait have
                no statutory id ⇒ field hidden. */}
            {editCountry.taxId && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{editCountry.taxId.label}</label>
                <Input
                  value={editForm.gstin}
                  onChange={(v) => setEditForm((f) => ({ ...f, gstin: v.toUpperCase().slice(0, editCountry.taxId?.maxLength ?? 30) }))}
                  inputClassName="font-mono"
                />
                {editErrors.gstin && <p className="text-xs text-danger-500 mt-1">{editErrors.gstin}</p>}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Payment Terms</label>
              <Select
                options={[...PAYMENT_TERMS_OPTIONS]}
                value={editForm.payment_terms}
                onChange={(v) => setEditForm((f) => ({ ...f, payment_terms: typeof v === 'string' ? v : 'NET_30' }))}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Address Line 1</label>
            <Input
              value={editForm.address_line1}
              onChange={(v) => setEditForm((f) => ({ ...f, address_line1: clip(v, ADDRESS_MAX) }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Address Line 2</label>
            <Input
              value={editForm.address_line2}
              onChange={(v) => setEditForm((f) => ({ ...f, address_line2: clip(v, ADDRESS_MAX) }))}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">City</label>
              <Input
                value={editForm.city}
                onChange={(v) => setEditForm((f) => ({ ...f, city: clip(v, CITY_MAX) }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{addressFields.regionLabel}</label>
              {/* A country with no region list (outside India and the GCC) gets free text. */}
              {addressFields.regionOptions.length > 0 ? (
                <Select
                  options={addressFields.regionOptions}
                  value={editForm.state}
                  onChange={(v) => setEditForm((f) => ({ ...f, state: typeof v === 'string' ? v : '' }))}
                  placeholder={addressFields.regionPlaceholder}
                />
              ) : (
                <Input
                  value={editForm.state}
                  onChange={(v) => setEditForm((f) => ({ ...f, state: clip(v, STATE_MAX) }))}
                  placeholder={addressFields.regionLabel}
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{addressFields.postalLabel}</label>
              <Input
                value={editForm.pincode}
                onChange={(v) => setEditForm((f) => ({ ...f, pincode: clampPostalInput(v, addressFields) }))}
                placeholder={addressFields.postalPlaceholder}
                inputMode={addressFields.postalIsFreeForm ? undefined : 'numeric'}
              />
              {editErrors.pincode && <p className="text-xs text-danger-500 mt-1">{editErrors.pincode}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Notes</label>
            <textarea
              className="w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3 text-sm text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none transition-colors"
              rows={3}
              value={editForm.notes}
              onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              maxLength={NOTES_MAX}
            />
            {editErrors.notes && <p className="text-xs text-danger-500 mt-1">{editErrors.notes}</p>}
          </div>
        </div>
      </Modal>

      {/* Deactivate Modal */}
      <Modal
        open={showDeactivateModal}
        onClose={() => { setShowDeactivateModal(false); setDeactivateReason(''); }}
        title="Deactivate Supplier"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setShowDeactivateModal(false); setDeactivateReason(''); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeactivate} disabled={deactivateMutation.isPending}>
              {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Are you sure you want to deactivate <strong className="text-neutral-900 dark:text-white">{supplier.name}</strong>?
            They will no longer appear when creating new purchase orders. Existing POs are preserved and you can reactivate later.
          </p>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Reason <span className="text-neutral-400 font-normal">(optional, audit log)</span>
            </label>
            <textarea
              className="w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3 text-sm text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-danger-500 focus:border-transparent resize-none transition-colors"
              rows={3}
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(clip(e.target.value, REASON_MAX))}
              placeholder="e.g. Vendor closed business; switched to alternate supplier"
              maxLength={REASON_MAX}
            />
            <p className="text-xs text-neutral-400 mt-1 text-right tabular-nums">
              {deactivateReason.length}/{REASON_MAX}
            </p>
          </div>
        </div>
      </Modal>

      {/* Reactivate Modal */}
      <Modal
        open={showReactivateModal}
        onClose={() => setShowReactivateModal(false)}
        title="Reactivate Supplier"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowReactivateModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleReactivate} disabled={reactivateMutation.isPending}>
              {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Reactivate <strong className="text-neutral-900 dark:text-white">{supplier.name}</strong>?
          They will become selectable again when creating new purchase orders.
        </p>
      </Modal>
    </div>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function SupplierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: supplier } = useSupplier(id);

  return (
    <PageContainer
      breadcrumbs={[
        { label: 'Inventory', href: '/inventory' },
        { label: 'Suppliers', href: '/inventory/suppliers' },
        { label: supplier?.name ?? 'Detail' },
      ]}
    >
      <ErrorBoundary>
        <SupplierDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
